import { withLogParticipants } from '../shared/log-participant-snapshot';
import { saveAppliedTruth, type TruthCommand } from '../shared/applied-truth-transport';
// minigame-truth.ts — browser-decided key actions and lockpicking, with committed state delivery.
//
// The pure resolvers (janus-engine/minigames/*) decide from the rows this page holds; this adapter commits ONE command
// to minigame_truth_commit_inc.php and adopts its applied row delta. The saved status identity repairs floor and carried
// facets on every peer; legacy presentation stamps must not introduce synthetic statuses into that held state.
// A handled key action NEVER reaches options/game/keys/use_key_inc.php (retired, R-### in docs/JANUS_CLIENT_TRUTH_STATUS.md).
import { resolveUseKey, USE_KEY_MESSAGES, type UseKeyCommand, type UseKeyInput } from '../janus-engine/minigames/use-key.js';
import { asRow, findRow, inventoryIdOf, lockIsLocked, MINIGAME_TRUTH_SCHEMA, statusEntries, statusItemOf, templateIdOf, upper, walkRows, LOCKED_STATUS_ID, LOCKPICK_TOOLS_ITEM_ID, LOCKPICKING_STATUS_ID, type Row } from '../janus-engine/minigames/rows.js';
import { ARC_MIN, ARC_MAX, KNIFE_DEAD_START, KNIFE_DEAD_END, PARTIAL_TURN_MAX, casterStats, feelFalloffForCaster, pickCount,
  requiredToolIds, skillBonusDegrees, struggleSecondsForCaster, toolsGate, type PickRow } from '../janus-engine/minigames/lockpick.js';
import { planLockpickBegin, planLockpickStep, type LockpickAttempt } from '../janus-engine/minigames/lockpick-activity.js';
import { activityContext, activityError, decideActivity, loadActivityStatus, recoverActivity, refreshActivity, saveActivity } from './activity-truth.js';
import { fetchAndCacheObject } from './object-cache.js';
import { fetchAndCacheCaster } from './caster-cache.js';
import { actorCanReachTarget } from '../shared/proximity-browser.js';
import { runRowOnly } from '../shared/actor-row-loader.js';
import { HandBack } from '../janus-engine/ability/context.js';
import { refuseUnported } from '../janus-engine/unported.js';
import { actorEncounterFor, actorRowFor, bankConfirmedRev, confirmedRevFor } from './movement-truth.js';
import { planGmInventoryAdd } from './gm-boundary-truth.js';
import { refreshSavedInventory } from '../shared/inventory-saved-refresh.js';
import type { BasePatchFrame } from '../janus-engine/compile/base-patch.js';
import { trapCommitFrame } from '../janus-engine/traps/trap-commit-frame.js';
import { abilityTruthCampaign } from './ability-truth.js';
import { possessorSceneContext } from './possessor-scene.js';
import { entityBasePending } from '../shared/compile-fetched-entity.js';

const ENDPOINT = '/options/game/actions/minigame_truth_commit_inc.php';
const RETRIES = 3;

/** Operations this browser sent (its own committed echo changes nothing it has not already adopted). */
const own = new Set<string>();
/** The page caches, typed locally (the global FateTide declaration differs per bundle project). */
const pageCaches = (): { objectCache?: unknown; casterCache?: unknown } => (window as unknown as { FateTide?: { objectCache?: unknown; casterCache?: unknown } }).FateTide ?? {};
const remember = (id: string): void => { own.add(id); if (own.size > 256) own.delete(own.values().next().value!); };

function objectCacheGet(id: string): Row | null {
  const cache = pageCaches().objectCache as { get?: (k: string) => unknown } | undefined;
  return asRow(cache?.get?.(id)) ?? asRow(cache?.get?.(id.toLowerCase()));
}
/** The actor's compiled row WITH its carried tree: the encounter member when it carries `inventory`, else the caster cache. */
function actorTreeFor(encounterId: string, actorId: string): Row | null {
  const member = actorRowFor(encounterId, actorId) as unknown as Row | null;
  if (member && Array.isArray(member.inventory)) return member;
  const want = upper(actorId);
  const caches = [pageCaches().casterCache, pageCaches().objectCache];
  for (const cache of caches) {
    if (!(cache instanceof Map)) continue;
    for (const value of cache.values()) {
      const row = asRow(value);
      if (row && upper(asRow(row.encounter_creatures)?.encounter_creature_id) === want && Array.isArray(row.inventory)) return row;
    }
  }
  return member;
}
function itemTypeReader(encounter: Row, actor: Row): (row: Row) => string {
  return (row: Row): string => {
    const direct = asRow(row.items)?.item_type;
    if (typeof direct === 'string' && direct) return direct;
    const tid = templateIdOf(row);
    for (const catalog of [asRow(row.catalog), asRow(actor.catalog), asRow(encounter.catalog)]) {
      const t = asRow(asRow(catalog?.[tid])?.items)?.item_type;
      if (typeof t === 'string' && t) return t;
    }
    const cached = asRow(objectCacheGet(tid)?.items)?.item_type;
    return typeof cached === 'string' ? cached : '';
  };
}
function stamp(ids: string[], locked: boolean): void {
  const fn = (window as unknown as { ftStampCachedInventoryLockState?: (id: string, locked: boolean) => number }).ftStampCachedInventoryLockState;
  if (typeof fn !== 'function') return;
  for (const id of new Set(ids.map(upper).filter(Boolean))) fn(id, locked);
}
/** Each row's OWN saved state (a chest held by a second lock stays locked — S1 Codex review #10). */
function stampStates(states: unknown): void {
  const map = asRow(states);
  if (!map) return;
  for (const [id, locked] of Object.entries(map)) if (typeof locked === 'boolean') stamp([id], locked);
}
export type UseKeyOutcome =
  | { outcome: 'handled'; locked: boolean; keyName: string; lockName: string; operationId: string }
  | { outcome: 'refused'; error: string; message: string; operationId?: string }
  | null;

/** The lock's unlock_attempt trap fire (trap-truth.ts buildFire via window.ftTrapBuildFire), null when nothing on the lock fires. */
async function trapFire(encounterId: string, lockId: string, actorId: string): Promise<Row | null> {
  const build = (window as Window & { ftTrapBuildFire?: (e: string, h: string, v: string, i: 'unlock_attempt') => Promise<Row | null> }).ftTrapBuildFire;
  if (!build || !lockId) return null;
  try { return await build(encounterId, lockId, actorId, 'unlock_attempt'); } catch { return null; }
}
/** A sprung unlock trap's saved victim columns on this page (trap-truth.ts applyVictim via window.ftTrapApplyVictim). */
function applyTrapVictim(encounterId: string, saved: unknown): void {
  try { (window as Window & { ftTrapApplyVictim?: (e: string, s: unknown) => void }).ftTrapApplyVictim?.(encounterId, saved); } catch { /* best effort */ }
}
async function post(command: UseKeyCommand | Row): Promise<{ status: number; body: Row | null } | null> {
  for (let attempt = 1; attempt <= RETRIES; attempt++) {
    try {
      const response = await saveAppliedTruth(ENDPOINT, command as TruthCommand, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(withLogParticipants(command)), signal: AbortSignal.timeout(20000) });
      let body: Row | null = null;
      try { body = await response.json() as Row; } catch { body = null; }
      if (response.status >= 500 && attempt < RETRIES) { await new Promise(r => setTimeout(r, 400 * attempt)); continue; }
      return { status: response.status, body };
    } catch {
      if (attempt < RETRIES) { await new Promise(r => setTimeout(r, 400 * attempt)); continue; }
    }
  }
  return null;
}

/**
 * "Unlock with Keys" / "Lock with Key". Null = this page cannot resolve the actor (no encounter row on the page) — the
 * caller shows its own failure; the retired legacy endpoint is never called. 'refused' carries the player-facing message.
 */
export async function tryCommitUseKey(input: UseKeyInput): Promise<UseKeyOutcome> {
  const encounterId = upper(input.encounterId), actorId = upper(input.actorId);
  const encounter = actorEncounterFor(encounterId) as Row | null;
  // Saved inventory deltas maintain the held tree, including key links while a card stays open.
  let actor = actorTreeFor(encounterId, actorId);
  if (!actor || entityBasePending(actor)) {
    await fetchAndCacheCaster(actorId, { throwOnError: true });
    actor = actorTreeFor(encounterId, actorId);
  }
  if (!encounter || !actor) return null;
  const member = actorRowFor(encounterId, actorId);
  const expectedRev = member ? confirmedRevFor(encounterId, actorId, member) : 0;
  const read = { itemType: itemTypeReader(encounter, actor), uuid: () => crypto.randomUUID().toUpperCase() };
  // Select before planning effects. Invalid keys never draw or dispatch an associated effect.
  const selection = resolveUseKey({ ...input, expectedRev }, encounter, actor, read);
  if (!selection.supported) return { outcome: 'refused', error: selection.error, message: selection.message };
  const ctx = activityContext(encounterId, actorId, actor);
  const addition = input.action === 'lock' ? await planGmInventoryAdd(encounterId, actorId, selection.command.lock_inventory_id,
    LOCKED_STATUS_ID, 0, 'permanent') : undefined;
  const scope = resolveUseKey({ ...input, expectedRev }, encounter, actor, { ...read, statusContext: ctx, statusAddition: addition });
  if (!scope.supported) {
    if (scope.error.startsWith('key:')) refuseUnported(scope.error);
    return { outcome: 'refused', error: scope.error, message: scope.message };
  }
  const command = scope.command as typeof scope.command & { trap_fire?: Row; applied_frame?: string };
  const snapshots = withLogParticipants(command).log_participants;
  snapshots[command.lock_inventory_id] = { name: command.lock_name };
  snapshots[command.key_inventory_id] = { name: command.key_name };
  const relock = command.key_action === 'lock';
  // An unlock springs any armed unlock_attempt trap on the lock: its fire is resolved HERE (trap-truth.ts — no trap rolls on the server).
  if (!relock) {
    const fire = await trapFire(encounterId, command.lock_inventory_id, actorId);
    if (fire?.mode === 'unmodelled') {
      const error = String(fire.reason ?? 'trap-fire:unmodelled');
      refuseUnported(error);
      return { outcome: 'refused', error, message: error };
    }
    if (fire) command.trap_fire = fire;
  }
  const host = findRow([encounter.encounter_inventories_arr, actor.inventory], command.lock_inventory_id)!.row;
  const applied_frame = trapCommitFrame({ ...command, fire: command.trap_fire }, encounter, actor, host,
    abilityTruthCampaign(encounter), ctx.read, (member, roster) => possessorSceneContext(encounter, member, roster));
  command.applied_frame = JSON.stringify(applied_frame);
  remember(command.operation_id);
  const { applied_frame: _savedFrame, ...wire } = command;
  window.ftPushAppliedTruth?.({ type: 'janus_minigame_truth', command: { ...wire, locked: relock }, applied_frame });
  window.dispatchEvent(new CustomEvent('ft:minigame-truth-applied', { detail: { command } }));
  const answer = await post(command);
  const body = answer?.body ?? null;
  if (answer && answer.status >= 200 && answer.status < 300 && body?.ok === true) {
    if (!window.ftPushAppliedTruth) refreshSavedInventory(body.base_refresh as BasePatchFrame | undefined);
    if (body.trap_victim) applyTrapVictim(encounterId, body.trap_victim);
    const locked = body.locked === true;
    window.dispatchEvent(new CustomEvent('ft:minigame-truth-committed', { detail: { command, result: body } }));
    return { outcome: 'handled', locked, keyName: String(body.key_name ?? scope.keyName ?? ''), lockName: String(body.lock_name ?? ''), operationId: command.operation_id };
  }
  const error = String(body?.error ?? (answer ? `http_${answer.status}` : 'network'));
  window.dispatchEvent(new CustomEvent('ft:minigame-truth-refused', { detail: { command, error, status: answer?.status ?? 0 } }));
  return { outcome: 'refused', error, message: USE_KEY_MESSAGES[error] ?? USE_KEY_MESSAGES[error.split(':')[0] ?? ''] ?? (relock ? 'Lock with key failed.' : 'Unlock with keys failed.'), operationId: command.operation_id };
}

export type MinigameTruthWireFrame = {
  schema: string; operation_id: string; encounter_id: string; actor_id: string; action: string; committed?: boolean; correction?: boolean;
  key_action?: 'unlock' | 'lock'; locked?: boolean; lock_inventory_id?: string; display_inventory_id?: string; stamp_ids?: string[]; rows?: Row[];
  actor_ops?: Row[]; aps?: number; consumed_inventory_id?: string | null; lock_states?: Record<string, boolean>; trap_victim?: unknown;
  base_refresh?: BasePatchFrame;
  pushed?: boolean;
};
/** Apply the committed lock state to every cached copy. */
export function handleMinigameTruth(command: MinigameTruthWireFrame): void {
  if (command?.schema !== MINIGAME_TRUTH_SCHEMA) return;
  if (command.pushed) {
    window.dispatchEvent(new CustomEvent('ft:minigame-truth-frame', { detail: { command, own: own.has(command.operation_id) } }));
    return;
  }
  if (command.action === 'activity' && command.committed) {
    // router.ts has already installed the browser's applied delta.
    if ((command as MinigameTruthWireFrame & { pushed?: boolean }).pushed) {
      const actor = actorRowFor(command.encounter_id, command.actor_id);
      const rev = (command as MinigameTruthWireFrame & { rev?: number }).rev;
      if (actor && Number.isInteger(rev))
        bankConfirmedRev(command.encounter_id, command.actor_id, actor, Number(rev));
      window.dispatchEvent(new CustomEvent('ft:activity-truth-frame', { detail: { command } }));
      return;
    }
    if (command.base_refresh && window.ftApplyBasePatch)
      void window.ftApplyBasePatch(command.base_refresh).catch(() => refreshActivity(command.encounter_id, command.actor_id));
    else void refreshActivity(command.encounter_id, command.actor_id);
    return;
  }
  // an unlock that sprang a trap: the victim's saved columns (a player character's only ride the frame)
  if (command.trap_victim) applyTrapVictim(command.encounter_id, command.trap_victim);
  if (command.action === 'key' && typeof command.locked === 'boolean') {
    refreshSavedInventory(command.base_refresh);
    if (!command.base_refresh?.applied) {
      if (asRow(command.lock_states)) stampStates(command.lock_states);
      else stamp(Array.isArray(command.stamp_ids) ? command.stamp_ids : [command.lock_inventory_id ?? '', command.display_inventory_id ?? ''], command.locked);
    }
  }
  window.dispatchEvent(new CustomEvent('ft:minigame-truth-frame', { detail: { command, own: own.has(command.operation_id) } }));
}

export interface LockpickSession {
  encounterId: string; actorId: string; lockId: string; rollPrivate: number; statusId: string;
  effZone: [number, number]; consumable: boolean; picks: PickRow[]; breaks: number; dice: number;
}
export type LockpickBeginOutcome =
  | { outcome: 'handled'; session: LockpickSession; config: Row }
  | { outcome: 'refused'; error: string; message: string };
const LOCKPICKING_ROLL_TYPE = 'CC2CEB6B-CEFB-481F-911A-A47A427D5EB2';
/** Shared read-only tools verdict for the door dial and the commit preflight. */
export async function lockpickToolsGate(encounter: Row, actor: Row, actorId: string, requires: unknown[] = []): Promise<ReturnType<typeof toolsGate>> {
  const roots = new Set<string>(), toolIds = new Set([LOCKPICK_TOOLS_ITEM_ID, ...requiredToolIds(requires)]);
  for (const { row, holders } of walkRows(encounter.encounter_inventories_arr)) if (toolIds.has(templateIdOf(row))) roots.add(holders[0] ?? inventoryIdOf(row));
  const reach = new Map<string, boolean | null>();
  await Promise.all([...roots].map(async id => { reach.set(id, await actorCanReachTarget(actorId, id)); }));
  return toolsGate(actor, encounter, requires, { itemType: itemTypeReader(encounter, actor), reachable: id => reach.get(upper(id)) ?? null });
}
async function lockpickHeld(encounterId: string, actorId: string, lockId: string, cancel = false) {
  await recoverActivity(encounterId, actorId);
  const encounter = actorEncounterFor(encounterId) as Row | null, actor = actorTreeFor(encounterId, actorId);
  const hit = encounter && actor && findRow([encounter.encounter_inventories_arr, actor.inventory], lockId);
  if (encounter && actor && cancel) return { ctx: activityContext(encounterId, actorId, actor), encounter, actor,
    lock: hit?.row ?? { inventory: { inventory_id: lockId } }, gate: { picks: [] as PickRow[], failure: null }, pickRows: [] as Row[] };
  if (!encounter || !actor || !hit) throw new HandBack('lockpicking:lock-or-actor-not-held');
  const def = objectCacheGet(templateIdOf(hit.row)) ?? asRow(await fetchAndCacheObject(templateIdOf(hit.row)));
  await fetchAndCacheObject(LOCKPICKING_STATUS_ID);
  const lock = def ? { ...hit.row, items: def.items, item_layer_types: def.item_layer_types, catalog: { ...asRow(hit.row.catalog), [templateIdOf(hit.row)]: def } } : hit.row;
  const ctx = activityContext(encounterId, actorId, actor);
  if (itemTypeReader(encounter, actor)(lock) !== 'lock' && upper(ctx.read.value(lock, 'roll_type')) !== LOCKPICKING_ROLL_TYPE) throw new HandBack('lockpicking:not-lockpickable');
  // W4 owns fire recording. Keep a precise refusal until its verdict is composed into this atomic activity save.
  if (['target', 'caster'].some(mode => statusEntries(lock, mode as 'target' | 'caster').some(s => statusItemOf(s) === '78C51D2D-5178-551C-1249-B5DDA4083098')))
    throw new HandBack('lockpicking:trap-save-not-composed');
  const gate = await lockpickToolsGate(encounter, actor, actorId, ctx.read.entries?.(lock, 'requires_tool') ?? []);
  const pickRows = [...walkRows(actor.inventory), ...walkRows(encounter.encounter_inventories_arr)].map(hit => hit.row);
  return { ctx, encounter, actor, lock, gate, pickRows };
}
/** One start save: AP, clock, first sweet spot and the resumable attempt are atomic. */
export async function lockpickBegin(input: { encounterId: string; actorId: string; lockInventoryId: string; rollPrivate?: number }): Promise<LockpickBeginOutcome | null> {
  return runRowOnly(input.actorId, async () => {
    try {
      const encounterId = upper(input.encounterId), actorId = upper(input.actorId), lockId = upper(input.lockInventoryId);
      const h = await lockpickHeld(encounterId, actorId, lockId);
      if (!lockIsLocked(h.lock)) throw new HandBack('lockpicking:not-locked');
      if (h.gate.failure) throw new HandBack('lockpicking:' + h.gate.failure);
      const prior = await loadActivityStatus(encounterId, actorId, LOCKPICKING_STATUS_ID);
      const plan = decideActivity(h.ctx, ctx => planLockpickBegin(ctx, h.actor, h.lock, prior));
      plan.rollPrivate = input.rollPrivate;
      if (plan.writes.length) await saveActivity(encounterId, actorId, 'lockpick_begin', plan);
      const session: LockpickSession = { encounterId, actorId, lockId, rollPrivate: Number(input.rollPrivate ?? 0), ...plan.attempt, picks: h.gate.picks };
      const stats = casterStats(key => h.ctx.read.value(h.actor, key));
      return { outcome: 'handled', session, config: { token: session, dice_count: session.dice, arc_min: ARC_MIN, arc_max: ARC_MAX,
        dead_start: KNIFE_DEAD_START, dead_end: KNIFE_DEAD_END, tension_zone_lo: session.effZone[0], tension_zone_hi: session.effZone[1],
        lockpick_break_seconds: struggleSecondsForCaster(session.dice, stats), skill_bonus_deg: skillBonusDegrees(stats), partial_turn_max: PARTIAL_TURN_MAX,
        partial_falloff_deg: feelFalloffForCaster(session.dice, stats), picks_remaining: pickCount(session.picks), picks_consumable: session.consumable } };
    } catch (error) { const answer = activityError(error); return { outcome: 'refused', error: answer.error, message: answer.error }; }
  });
}
export type LockpickStepOutcome = { ok: true; body: Row } | { ok: false; error: string; status: number; body: Row | null };
async function lockpickStep(session: LockpickSession, action: 'break' | 'cancel' | 'result', angle = 0, noStrain = false, keepalive = false): Promise<LockpickStepOutcome> {
  return runRowOnly(session.actorId, async () => {
    try {
      const h = await lockpickHeld(session.encounterId, session.actorId, session.lockId, action === 'cancel');
      const attempt: LockpickAttempt = { statusId: session.statusId, effZone: session.effZone, consumable: session.consumable, breaks: session.breaks, dice: session.dice };
      const plan = decideActivity(h.ctx, ctx => planLockpickStep(ctx, h.actor, h.lock, attempt, h.gate.picks, h.pickRows, action, angle, noStrain));
      plan.rollPrivate = session.rollPrivate;
      const saved = await saveActivity(session.encounterId, session.actorId, 'lockpick_' + action, plan, keepalive);
      Object.assign(session, plan.attempt);
      session.picks = h.gate.picks.map(p => ({ ...p, qty: p.qty - Number(p.inventoryId === plan.consumed) }));
      if (plan.success) stamp([session.lockId], false);
      return { ok: true, body: { ...saved, success: plan.success, consumed: !!plan.consumed, pick_broke: !!plan.consumed, session_over: plan.sessionOver } };
    } catch (error) { return { ...activityError(error), status: 409, body: null }; }
  });
}
export async function lockpickBreak(session: LockpickSession): Promise<{ ok: boolean; error?: string; picksRemaining: number; sessionOver: boolean }> {
  const out = await lockpickStep(session, 'break');
  return { ok: out.ok, ...(!out.ok ? { error: out.error } : {}), picksRemaining: pickCount(session.picks), sessionOver: out.ok && out.body.session_over === true };
}
export async function lockpickCancel(session: LockpickSession, _reason: string, keepalive = false): Promise<LockpickStepOutcome> { return lockpickStep(session, 'cancel', 0, false, keepalive); }
export async function lockpickResult(session: LockpickSession, angleDeg: number, noStrain: boolean): Promise<LockpickStepOutcome & { success?: boolean }> {
  const out = await lockpickStep(session, 'result', angleDeg, noStrain); return { ...out, success: out.ok && out.body.success === true };
}

Object.assign(window, { ftTryCommitUseKey: tryCommitUseKey, ftLockpickBegin: lockpickBegin, ftLockpickBreak: lockpickBreak, ftLockpickCancel: lockpickCancel, ftLockpickResult: lockpickResult });
